'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface Profile {
  full_name: string;
  email: string;
  phone: string | null;
  loyalty_points: number;
  lifetime_points: number;
  created_at: string;
}

export default function CustomerProfilePage() {
  const router = useRouter();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ full_name: '', phone: '' });
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [userId, setUserId] = useState('');

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }
      setUserId(user.id);
      supabase.from('user_profiles').select('full_name, email, phone, loyalty_points, lifetime_points, created_at').eq('id', user.id).maybeSingle()
        .then(({ data }) => {
          if (data) {
            setProfile(data);
            setForm({ full_name: data.full_name || '', phone: data.phone || '' });
          }
          setLoading(false);
        });
    });
  }, [router]);

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.from('user_profiles').update({ full_name: form.full_name, phone: form.phone || null }).eq('id', userId);
    if (error) {
      showToast('Failed to update profile.', 'error');
    } else {
      setProfile(p => p ? { ...p, full_name: form.full_name, phone: form.phone || null } : p);
      showToast('Profile updated successfully!', 'success');
      setEditing(false);
    }
    setSaving(false);
  };

  return (
    <CustomerDashboardLayout>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-500 shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">My Profile</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage your personal information</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Avatar */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center gap-5">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-700 text-primary">
                  {(profile?.full_name || 'C').slice(0, 2).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-lg font-700 text-slate-900">{profile?.full_name || 'Customer'}</p>
                <p className="text-sm text-slate-500">{profile?.email}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Member since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '—'}
                </p>
              </div>
            </div>

            {/* Loyalty */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="StarIcon" size={18} className="text-amber-500" />
                  <span className="text-sm font-600 text-amber-700">Loyalty Points</span>
                </div>
                <p className="text-2xl font-700 text-amber-700">{(profile?.loyalty_points || 0).toLocaleString('en-IN')}</p>
                <p className="text-xs text-amber-600 mt-1">Available to redeem</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="TrophyIcon" size={18} className="text-purple-500" />
                  <span className="text-sm font-600 text-purple-700">Lifetime Points</span>
                </div>
                <p className="text-2xl font-700 text-purple-700">{(profile?.lifetime_points || 0).toLocaleString('en-IN')}</p>
                <p className="text-xs text-purple-600 mt-1">Total earned</p>
              </div>
            </div>

            {/* Edit form */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-700 text-slate-900">Personal Information</h2>
                {!editing && (
                  <button onClick={() => setEditing(true)} className="text-sm text-primary font-600 hover:underline flex items-center gap-1">
                    <Icon name="PencilIcon" size={14} /> Edit
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-600 text-slate-500 mb-1.5">Full Name</label>
                  {editing ? (
                    <input
                      type="text"
                      value={form.full_name}
                      onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  ) : (
                    <p className="text-sm font-500 text-slate-900">{profile?.full_name || '—'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-600 text-slate-500 mb-1.5">Email Address</label>
                  <p className="text-sm font-500 text-slate-900">{profile?.email}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Email cannot be changed</p>
                </div>
                <div>
                  <label className="block text-xs font-600 text-slate-500 mb-1.5">Phone Number</label>
                  {editing ? (
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  ) : (
                    <p className="text-sm font-500 text-slate-900">{profile?.phone || '—'}</p>
                  )}
                </div>

                {editing && (
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600 disabled:opacity-60 flex items-center gap-2"
                    >
                      {saving ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" /> Saving…</> : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => { setEditing(false); setForm({ full_name: profile?.full_name || '', phone: profile?.phone || '' }); }}
                      className="px-6 py-2.5 rounded-xl text-sm font-600 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </CustomerDashboardLayout>
  );
}
